/**
 * The command Overview's figures, assembled from the demo engine. Each builder
 * returns exactly the `ApiClient` response shape the real endpoint will.
 */

import type {
  ChannelKind,
  ChannelPerformance,
  CompetitionPerformance,
  FunnelStage,
  FunnelStageFigures,
  FunnelStageKey,
  Kpi,
  KpiKey,
  PacingStatus,
  PeriodInfo,
  RevenueTrend,
  TrendWindow,
} from '@/api/types'
import { FUNNEL_STAGES, FUNNEL_STAGE_LABELS, KPI_KEYS, KPI_PRESENTATION, KPI_SPARKLINE_SOURCES } from '@/api/types'
import type { DrillDown } from '@/lib/drilldown'
import {
  addDays,
  formatDateKey,
  resolvePeriod,
  type DateWindow,
  type GlobalFilters,
  type ResolvedPeriod,
} from '@/lib/filters'

import {
  ARCHIVED,
  elapsedDayShare,
  hourShare,
  round2,
  scopeFor,
  type DemoDataset,
  type PeriodTotals,
  type Scope,
  type Totals,
} from './engine'
import { signupsIn, signupsOn } from './signups'

export interface Built<T> {
  data: T
  period?: PeriodInfo
}

export function periodInfo(period: ResolvedPeriod): PeriodInfo {
  return {
    from: period.current.from,
    to: period.current.to,
    label: period.label,
    compareLabel: period.compareLabel,
    previousFrom: period.previous?.from ?? null,
    previousTo: period.previous?.to ?? null,
  }
}

/** The current and comparison totals for the filters, comparison aligned to "by this time". */
export function currentAndPrevious(
  dataset: DemoDataset,
  filters: GlobalFilters,
): { period: ResolvedPeriod; scope: Scope; current: PeriodTotals; previous: PeriodTotals | null } {
  const period = resolvePeriod(filters, dataset.now)
  const scope = scopeFor(dataset.tenant, filters)
  const current = dataset.period(period.current, scope)
  const partial = period.current.to === dataset.today ? elapsedDayShare(dataset.now) : undefined
  const previous = period.previous ? dataset.period(period.previous, scope, partial) : null
  return { period, scope, current, previous }
}

type MetricValues = Record<Exclude<KpiKey, 'active_competitions' | 'automations_running' | 'signups'>, number>

function metricValues(totals: Totals): MetricValues {
  return {
    revenue: totals.revenue,
    orders: totals.orders,
    tickets: totals.tickets,
    new_customers: totals.newCustomers,
    returning_customers: totals.returningCustomers,
    aov: totals.orders > 0 ? round2(totals.revenue / totals.orders) : 0,
    conversion_rate: totals.stages.visitors > 0 ? totals.orders / totals.stages.visitors : 0,
    ad_spend: totals.adSpend,
    attributed_revenue: totals.attributedRevenue,
    roas: totals.adSpend > 0 ? totals.attributedRevenue / totals.adSpend : 0,
  }
}

/**
 * The demo's Overview shapes. The mock generates every figure, so none of the
 * live client's nulls ("not available", "not reported") applies to it; the
 * demo generators and the screens' demo tests read these.
 */
export type DemoKpi = Kpi & { value: number }
export type DemoCompetitionPerformance = CompetitionPerformance & {
  category: string
  conversionRate: number
  sellThrough: number
  velocityPerDay: number
  pacing: PacingStatus
  closesOn: string
}
export type DemoChannelPerformance = ChannelPerformance & { kind: ChannelKind; spend: number }

/** The demo's tiles: the shared presentation, in `KPI_KEYS` order. */
export const KPI_DEFINITIONS = KPI_KEYS.map((key) => ({ key, ...KPI_PRESENTATION[key] }))

/** Short windows get a fortnight of context so the sparkline has a shape. */
function sparklineWindow(current: DateWindow): DateWindow {
  return current.days >= 7 ? current : { from: addDays(current.to, -13), to: current.to, days: 14 }
}

export function buildKpis(dataset: DemoDataset, filters: GlobalFilters): Built<DemoKpi[]> {
  const { period, scope, current, previous } = currentAndPrevious(dataset, filters)
  const now = metricValues(current)
  const before = previous ? metricValues(previous) : null
  const sparkDays = dataset.period(sparklineWindow(period.current), scope).days
  const running = dataset.tenant.automations.filter((automation) => automation.status === 'running').length
  const { seed } = dataset.tenant

  const data = KPI_DEFINITIONS.map((definition): DemoKpi => {
    const base = { ...definition }
    // A tile whose sparkline another route draws carries none of its own, as on the live client.
    const sparkline = (values: () => number[]) => (KPI_SPARKLINE_SOURCES[definition.key] ? [] : values())
    switch (definition.key) {
      case 'active_competitions':
        return {
          ...base,
          value: dataset.activeCount(period.current.to, scope),
          previous: period.previous ? dataset.activeCount(period.previous.to, scope) : null,
          sparkline: sparkline(() => sparkDays.map((day) => dataset.activeCount(day.date, scope))),
        }
      case 'automations_running':
        return { ...base, value: running, previous: null, sparkline: sparkline(() => sparkDays.map(() => running)) }
      case 'signups':
        return {
          ...base,
          value: signupsIn(seed, current.days),
          previous: previous ? signupsIn(seed, previous.days) : null,
          sparkline: sparkline(() => sparkDays.map((day) => signupsOn(seed, day))),
        }
      default: {
        const key = definition.key
        return {
          ...base,
          value: now[key],
          previous: before ? before[key] : null,
          sparkline: sparkline(() => sparkDays.map((day) => metricValues(day)[key])),
        }
      }
    }
  })

  return { data, period: periodInfo(period) }
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

export function buildRevenueTrend(
  dataset: DemoDataset,
  filters: GlobalFilters,
  window: TrendWindow,
): Built<RevenueTrend> {
  // The card's own window replaces the global date range; every other filter,
  // and the comparison choice, still applies.
  const trendFilters: GlobalFilters = { ...filters, range: window, from: null, to: null }
  const { period, current, previous } = currentAndPrevious(dataset, trendFilters)
  const drillDown: DrillDown = { path: '/analytics/revenue' }

  if (window === 'today') {
    // Spread each total over the hours so far, so the points add up to the
    // headline figure (and to the Revenue tile for "today").
    const hour = dataset.now.getHours()
    const minuteShare = dataset.now.getMinutes() / 60
    const elapsed = elapsedDayShare(dataset.now)
    const share = (index: number) =>
      elapsed > 0 ? (hourShare(index) * (index === hour ? minuteShare : 1)) / elapsed : 0
    const points = Array.from({ length: hour + 1 }, (_, index) => ({
      at: `${period.current.from}T${pad(index)}:00`,
      label: `${pad(index)}:00`,
      current: round2(current.revenue * share(index)),
      previous: previous ? round2(previous.revenue * share(index)) : null,
    }))
    return {
      data: {
        window,
        granularity: 'hour',
        total: current.revenue,
        previousTotal: previous ? previous.revenue : null,
        compareLabel: period.compareLabel,
        points,
        drillDown,
      },
      period: periodInfo(period),
    }
  }

  const points = current.days.map((day, index) => ({
    at: day.date,
    label: formatDateKey(day.date),
    current: day.revenue,
    previous: previous ? (previous.days[index]?.revenue ?? null) : null,
  }))
  return {
    data: {
      window,
      granularity: 'day',
      total: current.revenue,
      previousTotal: previous ? previous.revenue : null,
      compareLabel: period.compareLabel,
      points,
      drillDown,
    },
    period: periodInfo(period),
  }
}

/**
 * Distinct users per session at each stage: a visitor may come back in a second
 * session before buying, so users trail sessions most at the top of the funnel.
 */
const USERS_PER_SESSION: Record<FunnelStageKey, number> = {
  visitors: 0.84,
  competition_viewed: 0.86,
  ticket_selector_used: 0.9,
  add_to_basket: 0.92,
  checkout_started: 0.95,
  payment_attempted: 0.97,
  purchase_completed: 0.98,
}

function rate(numerator: number, denominator: number): number | null {
  return denominator > 0 ? Math.round((numerator / denominator) * 10_000) / 10_000 : null
}

/** One period's stage figures by the API's own rules (`modules/funnel/README.md`), rounded as it rounds them. */
export function funnelFigures(sessions: Record<FunnelStageKey, number>): Record<FunnelStageKey, FunnelStageFigures> {
  const top = sessions.visitors
  const figures = {} as Record<FunnelStageKey, FunnelStageFigures>
  FUNNEL_STAGES.forEach((key, index) => {
    const count = sessions[key]
    const before = index > 0 ? sessions[FUNNEL_STAGES[index - 1] as FunnelStageKey] : null
    const dropOff = before === null ? null : before - count
    figures[key] = {
      users: Math.round(count * USERS_PER_SESSION[key]),
      sessions: count,
      stageConversionRate: before === null ? null : rate(count, before),
      totalConversionRate: rate(count, top),
      dropOff,
      dropOffRate: before === null || dropOff === null ? null : rate(dropOff, before),
    }
  })
  return figures
}

export function buildFunnel(dataset: DemoDataset, filters: GlobalFilters): Built<FunnelStage[]> {
  const { period, current, previous } = currentAndPrevious(dataset, filters)
  const now = funnelFigures(current.stages)
  // Every figure, `count` and `previousCount` included, comes from one place: `funnelFigures`.
  const before = previous ? funnelFigures(previous.stages) : null
  const data = FUNNEL_STAGES.map((key) => ({
    key,
    label: FUNNEL_STAGE_LABELS[key],
    ...now[key],
    count: now[key].sessions,
    previousCount: before ? before[key].sessions : null,
    previous: before ? before[key] : null,
    drillDown: { path: '/analytics/funnel', params: { stage: key } },
  }))
  return { data, period: periodInfo(period) }
}

export function buildTopCompetitions(
  dataset: DemoDataset,
  filters: GlobalFilters,
  limit: number,
): Built<DemoCompetitionPerformance[]> {
  const { period, current } = currentAndPrevious(dataset, filters)
  const data = dataset.tenant.competitions
    .flatMap((competition): DemoCompetitionPerformance[] => {
      const totals = current.competitions.get(competition.id)
      if (!totals || totals.tickets === 0) return []
      const status = dataset.competitionStatus(competition)
      const entity = { type: 'competition' as const, id: competition.id, label: competition.name }
      return [
        {
          entity,
          category: competition.category,
          revenue: totals.revenue,
          ticketsSold: totals.tickets,
          conversionRate: totals.views > 0 ? totals.orders / totals.views : 0,
          sellThrough: status.sellThrough,
          velocityPerDay: Math.round(status.velocityPerDay),
          pacing: status.pacing,
          closesOn: status.closes,
          drillDown: { path: `/analytics/competitions/${competition.id}`, entity },
        },
      ]
    })
    .sort((a, b) => b.revenue - a.revenue || a.entity.id.localeCompare(b.entity.id))
    .slice(0, limit)
  return { data, period: periodInfo(period) }
}

export function buildChannels(dataset: DemoDataset, filters: GlobalFilters): Built<DemoChannelPerformance[]> {
  const { period, current } = currentAndPrevious(dataset, filters)
  const data = dataset.tenant.channels
    .flatMap((channel): DemoChannelPerformance[] => {
      const totals = current.channels.get(channel.id)
      if (!totals) return []
      const entity = { type: 'channel' as const, id: channel.id, label: channel.name }
      return [
        {
          entity,
          kind: channel.kind,
          spend: totals.spend,
          attributedRevenue: totals.attributedRevenue,
          orders: totals.orders,
          cpa: totals.spend > 0 && totals.orders > 0 ? round2(totals.spend / totals.orders) : null,
          roas: totals.spend > 0 ? totals.attributedRevenue / totals.spend : null,
          drillDown: { path: '/marketing/channels', entity },
        },
      ]
    })
    .sort((a, b) => b.attributedRevenue - a.attributedRevenue || a.entity.id.localeCompare(b.entity.id))
  return { data, period: periodInfo(period) }
}

export { ARCHIVED }
